import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type SurveyGroupCustomerAttribute as SurveyGroupCustomerAttributeBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type SurveyGroupCustomerAttribute = App.PickGraphQL<
  SurveyGroupCustomerAttributeBase,
  'name'
> & {
  idLike: string
}

export type SurveyGroupCustomerAttributeSelectorProps<
  TSurveyGroupCustomerAttribute extends SurveyGroupCustomerAttribute,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  customerAttributes: TSurveyGroupCustomerAttribute[]
  onChange?: (
    customerAttributeIdLike:
      TSurveyGroupCustomerAttribute['idLike'] | Unassigned,
  ) => void
  value: TSurveyGroupCustomerAttribute['idLike'] | Unassigned
}

export const SurveyGroupCustomerAttributeSelector = React.forwardRef(
  <TSurveyGroupCustomerAttribute extends SurveyGroupCustomerAttribute>(
    {
      customerAttributes,
      onChange,
      value: valueProp,
      ...rest
    }: SurveyGroupCustomerAttributeSelectorProps<TSurveyGroupCustomerAttribute>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const value = React.useMemo(
      () =>
        customerAttributes.find(({ idLike }) => idLike === valueProp) ?? {
          idLike: unassignedSymbol,
        },
      [customerAttributes, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (customerAttribute: TSurveyGroupCustomerAttribute) => {
        onChange?.(customerAttribute.idLike)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={customerAttributes}
        dataItemKey="idLike"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
